<template>
  <ContextColorProvider context="trade">
    <div class="highlight-detail__container">
      <div v-if="error" class="highlight-detail__error-container">
        <ActionableHelperView
          class="highlight-detail__error-container__error"
          :on-retry="load"
        />
      </div>
      <HeroContainer v-else>
        <template #navbar="{ isNavbarTransparent }">
          <NavBar :transparent="isNavbarTransparent">
            <template #left>
              <button aria-label="Voltar" title="Voltar" @click="onBackClick">
                <RoundedIcon
                  icon-name="ic_arrow_left"
                  main-color="transparent"
                  :hover-color="NebraskaColors.backgroundHover"
                  :text-color="
                    isNavbarTransparent
                      ? NebraskaColors.textOverImage
                      : NebraskaColors.elementPrimary
                  "
                  :hover-text-color="
                    isNavbarTransparent
                      ? NebraskaColors.textOverImage
                      : NebraskaColors.elementPrimary
                  "
                  :has-hover="true"
                />
              </button>
            </template>
            <template #center>
              <SkeletonLoader v-if="loading" show-animation>
                <div class="loading-skeleton__navbar" />
              </SkeletonLoader>
              <h2 v-else class="text-p6-semibold">
                {{ highlightData.category }}
              </h2>
            </template>
          </NavBar>
        </template>
        <template #hero>
          <Hero
            :image-small="computedImage.small"
            :image-medium="computedImage.medium"
            :image-large="computedImage.large"
            height="50vh"
            show-overlay
          >
            <div class="highlight-detail__description">
              <SkeletonLoader v-if="loading" show-animation>
                <div class="loading-skeleton__title" />
                <div class="loading-skeleton__description" />
              </SkeletonLoader>
              <template v-else>
                <p class="text-h-1">{{ highlightData.title }}</p>
                <p class="text-p-5">{{ highlightData.description }}</p>
              </template>
            </div>
          </Hero>
        </template>
        <template #content>
          <div class="highlight-detail__content-block">
            <div class="highlight-detail__content">
              <SkeletonLoader v-if="loading" show-animation>
                <div class="loading-skeleton__text" />
                <div
                  v-for="index in 5"
                  :key="index"
                  class="loading-skeleton__item"
                />
              </SkeletonLoader>
              <template v-else>
                <p class="text-p-6 highlight-detail__warning">
                  Esta é uma lista ordenada por popularidade. Não é uma sugestão
                  de investimento.
                </p>
                <InstrumentList
                  :on-row-click="onRowClick"
                  :search-items-data="highlightData.items"
                />
              </template>
            </div>
          </div>
        </template>
      </HeroContainer>
    </div>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

import {
  NavBar,
  ContextColorProvider,
  ActionableHelperView,
  RoundedIcon,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { Hero, HeroContainer } from '@/modules/common/components/Hero';
import { InstrumentList } from '@/modules/trade/components/InstrumentList';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  HighLightsDetail,
  getHighLightsDetail,
  Instrument
} from '@/modules/trade/services/search';

import { trackInstrumentClick, trackSearchHighlightViewLoad } from './trackers';

@Component({
  components: {
    ContextColorProvider,
    HeroContainer,
    ActionableHelperView,
    InstrumentList,
    NavBar,
    Hero,
    RoundedIcon,
    SkeletonLoader
  }
})
export default class SearchHighLightView extends Vue {
  loading = true;
  error = false;
  highlightData: HighLightsDetail | null = null;
  readonly NebraskaColors = NebraskaColors;

  async created() {
    await this.load();
    trackSearchHighlightViewLoad(this.highlightData?.title || '');
  }

  onBackClick() {
    this.$router.back();
  }

  async load() {
    this.loading = true;
    this.error = false;
    try {
      const data = await getHighLightsDetail(this.$route.params.id);
      this.highlightData = data;
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
    }
  }

  get computedImage() {
    return {
      small: this.highlightData?.image?.small,
      medium: this.highlightData?.image?.medium,
      large: this.highlightData?.image?.large
    };
  }

  onRowClick(instrument: Instrument) {
    trackInstrumentClick(
      this.highlightData!.title,
      instrument.symbol,
      instrument.name!
    );

    this.$router.push({
      name: 'tradeInstrument',
      params: { ticker: instrument.symbol }
    });
  }
}
</script>
<style lang="less" scoped>
.highlight-detail {
  &__warning {
    color: @text-secondary;
    margin-bottom: @size-spacing-x400;
  }
  &__description {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;

    p {
      color: @element-over-image;
    }
  }

  &__content-block {
    margin: @size-spacing-x800 auto 0;
    max-width: 1200px;
    width: 100%;
    background: @background-secondary;
  }

  &__content {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
    padding: 0 @size-spacing-x400 @size-spacing-x1100;

    .from-md({padding: 0 0 @size-spacing-x1100;});
  }
}

.loading-skeleton {
  &__navbar {
    height: @size-spacing-x400;
    width: 50%;
    border-radius: 4px;
    margin: 0 auto;
  }

  &__title {
    height: @size-spacing-x800;
    width: 40%;
    margin-bottom: @size-spacing-x300;
    border-radius: 12px;
  }

  &__description {
    height: @size-spacing-x500;
    width: 80%;
    border-radius: 8px;
  }

  &__text {
    height: @size-spacing-x400;
    width: 70%;
    border-radius: 4px;
    margin-bottom: @size-spacing-x500;
  }

  &__item {
    height: @size-spacing-x800;
    width: 100%;
    border-radius: 8px;
    margin-bottom: @size-spacing-x500;
  }
}
</style>
